import {
  Alert,
  Badge,
  Button,
  type Column,
  ColourPicker,
  DataTable,
  Dialog,
  EmptyState,
  fieldErrors,
  LinkButton,
  Menu,
  PageHeader,
  type Problem,
  problemText,
  readJson,
  readProblem,
  Section,
  Select,
  Skeleton,
  TextField,
  type Tone,
  useActionKey,
  usePaged,
  useWords,
} from "@emdur/ui";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi } from "../lib/apiContext";
import { BASE_COLOURS, type BaseColour, type Brand, type BrandSummary, brandPath, brandsPath, COLOUR_LABELS } from "../lib/brands";
import { failureText } from "../lib/errors";

/** A new brand starts from Emdur's own four colours (BRAND.md); the person changes them before creating. */
const STARTING_COLOURS: Record<BaseColour, string> = { primary: "#5B5BE6", secondary: "#1FB6E0", accent: "#F5A623", neutral: "#15171C" };

/** A brand's state as the words and tone the list shows. */
function brandStatus(state: BrandSummary["state"]): { label: string; tone: Tone } {
  if (state === "published") return { label: "Published", tone: "positive" };
  if (state === "archived") return { label: "Archived", tone: "warning" };
  return { label: "Draft", tone: "neutral" };
}

type Pending = { kind: "duplicate" | "archive" | "delete"; brand: BrandSummary } | null;

/** What an action came to: the answer, or the refusal in the problem's words with the fields it names. */
type Outcome = { ok: true; response: Response } | { ok: false; text: string; errors: Record<string, string> };

/** One action under its own Idempotency-Key: the key is kept for a retry after a failure and spent on success. A
 * refusal comes back in the problem's words (and its field errors); a request that never arrived in the kit's words. */
function useAct(): (action: string, path: string, method: string, body?: unknown) => Promise<Outcome> {
  const api = useApi();
  const keys = useActionKey();
  const words = useWords();
  return async (action, path, method, body) => {
    const key = keys.keyFor(action, body);
    try {
      const headers: Record<string, string> = { "Idempotency-Key": key };
      if (body !== undefined) headers["Content-Type"] = "application/json";
      const response = await api.fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      if (!response.ok) {
        const problem: Problem = await readProblem(response, words);
        return { ok: false, text: problemText(problem), errors: fieldErrors(problem) };
      }
      keys.spent(action, key);
      return { ok: true, response };
    } catch (error) {
      return { ok: false, text: failureText(error, words), errors: {} };
    }
  };
}

export function Brands() {
  const { id = "" } = useParams();
  const api = useApi();
  const act = useAct();
  const navigate = useNavigate();
  const [show, setShow] = useState<"active" | "archived">("active");
  const list = usePaged<BrandSummary>(show === "archived" ? `${brandsPath(id)}?state=archived` : brandsPath(id), api.fetch);
  const [orgName, setOrgName] = useState("");
  const [message, setMessage] = useState("");
  const [refusal, setRefusal] = useState("");
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending>(null);

  useEffect(() => {
    readJson<{ display_name: string }>(api.fetch, `/v1/organizations/${id}`).then(
      (answer) => setOrgName(answer.data.display_name),
      () => setOrgName(""),
    );
  }, [api.fetch, id]);

  /** An action on a brand in the list: its outcome shows above the list, and the list is read again. */
  async function run(action: string, path: string, method: string, body: unknown, done: string): Promise<void> {
    setRefusal("");
    setMessage("");
    const outcome = await act(action, path, method, body);
    if (!outcome.ok) {
      setRefusal(outcome.text);
      return;
    }
    setMessage(done);
    await list.reload();
  }

  const menu = (b: BrandSummary) => (
    <Menu
      trigger={
        <>
          {"Actions "}
          <span className="sr-only">for {b.name}</span>
        </>
      }
      label={`Actions for ${b.name}`}
      items={[
        { id: "open", label: "Open", href: `/orgs/${id}/brands/${b.id}` },
        { id: "duplicate", label: "Duplicate…", onAction: () => setPending({ kind: "duplicate", brand: b }) },
        {
          id: "default",
          label: "Make default",
          isDisabled: b.state !== "published" || b.is_default,
          onAction: () => void run(`default:${b.id}`, `${brandPath(id, b.id)}/default`, "POST", undefined, `${b.name} is now the default brand.`),
        },
        { id: "archive", label: "Archive…", isDisabled: b.is_default || b.state === "archived", onAction: () => setPending({ kind: "archive", brand: b }) },
        { id: "delete", label: "Delete…", isDanger: true, isDisabled: b.is_default, onAction: () => setPending({ kind: "delete", brand: b }) },
      ]}
    />
  );

  const columns: Column<BrandSummary>[] = [
    {
      id: "name",
      label: "Name",
      isRowHeader: true,
      sortValue: (b) => b.name,
      cell: (b) => (
        <LinkButton variant="quiet" href={`/orgs/${id}/brands/${b.id}`}>
          {b.name}
        </LinkButton>
      ),
    },
    {
      id: "state",
      label: "State",
      cell: (b) => {
        const status = brandStatus(b.state);
        return <Badge tone={status.tone}>{status.label}</Badge>;
      },
    },
    { id: "default", label: "Default brand", cell: (b) => (b.is_default ? <Badge tone="accent">Default</Badge> : <span className="text-muted">No</span>) },
    { id: "version", label: "Current version", sortValue: (b) => b.current_version, cell: (b) => (b.current_version ? `Version ${b.current_version}` : "Not published") },
    { id: "actions", label: "Actions", cell: menu },
  ];

  const close = () => setPending(null);
  return (
    <>
      <PageHeader
        eyebrow={orgName || undefined}
        title="Brands"
        description="An organisation's look: colours, fonts, logos and shape. Change a draft freely; publish it as a numbered version that pages and apps load."
        actions={
          <Button variant="primary" onPress={() => setCreating(true)}>
            Create brand
          </Button>
        }
      />
      <div className="flex flex-col gap-4">
        {message && <Alert tone="positive">{message}</Alert>}
        {refusal && <Alert tone="danger">{refusal}</Alert>}
        {list.error && <Alert tone="danger">{list.error}</Alert>}
        <div className="max-w-xs">
          <Select
            label="Show"
            options={[
              { value: "active", label: "Drafts and published brands" },
              { value: "archived", label: "Archived brands" },
            ]}
            value={show}
            onChange={(value) => setShow(value === "archived" ? "archived" : "active")}
          />
        </div>
        <Section flush>
          {list.items === null ? (
            <div className="p-5">
              <Skeleton label="Loading brands" />
            </div>
          ) : list.items.length === 0 ? (
            <EmptyState
              title={show === "archived" ? "No archived brands" : "No brands yet"}
              action={
                show === "active" ? (
                  <Button variant="primary" onPress={() => setCreating(true)}>
                    Create the first brand
                  </Button>
                ) : undefined
              }
            >
              A brand starts from four colours; fonts, logos and shape follow in the editor.
            </EmptyState>
          ) : (
            <DataTable caption="Brands" columns={columns} rows={list.items} rowKey={(b) => b.id} />
          )}
        </Section>
        {list.more && (
          <div>
            <Button onPress={() => void list.more?.()} isLoading={list.loading}>
              Show more
            </Button>
          </div>
        )}
      </div>
      <CreateBrand org={id} isOpen={creating} onOpenChange={setCreating} onCreated={(brand) => void navigate(`/orgs/${id}/brands/${brand}`)} />
      {pending?.kind === "duplicate" && (
        <DuplicateBrand
          brand={pending.brand}
          onClose={close}
          run={(name) => run(`duplicate:${pending.brand.id}`, `${brandPath(id, pending.brand.id)}/duplicate`, "POST", { name }, `${name} is created as a draft.`)}
        />
      )}
      {pending?.kind === "archive" && (
        <ConfirmAction
          title={`Archive ${pending.brand.name}?`}
          confirmLabel="Archive brand"
          onClose={close}
          run={() => run(`archive:${pending.brand.id}`, `${brandPath(id, pending.brand.id)}/archive`, "POST", undefined, `${pending.brand.name} is archived.`)}
        >
          An archived brand leaves this list and can no longer be changed. Its published versions and their links keep working.
        </ConfirmAction>
      )}
      {pending?.kind === "delete" && (
        <ConfirmAction
          title={`Delete ${pending.brand.name}?`}
          confirmLabel="Delete brand"
          typeToConfirm={pending.brand.name}
          onClose={close}
          run={() => run(`delete:${pending.brand.id}`, brandPath(id, pending.brand.id), "DELETE", undefined, `${pending.brand.name} is deleted.`)}
        >
          The brand leaves this list for good. Its published versions, their links and their files keep working.
        </ConfirmAction>
      )}
    </>
  );
}

function CreateBrand({ org, isOpen, onOpenChange, onCreated }: { org: string; isOpen: boolean; onOpenChange: (open: boolean) => void; onCreated: (brand: string) => void }) {
  const act = useAct();
  const keys = useActionKey();
  const [name, setName] = useState("");
  const [colors, setColors] = useState(STARTING_COLOURS);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState("");
  const [busy, setBusy] = useState(false);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (keys.sending.current) return; // a second press while the first is out sends nothing
    keys.sending.current = true;
    setBusy(true);
    setErrors({});
    setRefusal("");
    try {
      const outcome = await act("create", brandsPath(org), "POST", { name, source: { colors } });
      if (!outcome.ok) {
        setErrors(outcome.errors);
        setRefusal(outcome.text);
        return;
      }
      onCreated(((await outcome.response.json()) as Brand).id);
    } finally {
      keys.sending.current = false;
      setBusy(false);
    }
  }

  return (
    <Dialog title="Create a brand" isOpen={isOpen} onOpenChange={onOpenChange}>
      <form onSubmit={create} className="flex flex-col gap-4">
        <TextField label="Name" value={name} onChange={setName} isRequired maxLength={100} error={errors["/name"]} />
        {BASE_COLOURS.map((colour) => (
          <ColourPicker
            key={colour}
            label={`${COLOUR_LABELS[colour]} colour`}
            value={colors[colour]}
            onChange={(value) => setColors({ ...colors, [colour]: value })}
            error={errors[`/source/colors/${colour}`]}
          />
        ))}
        {refusal && <Alert tone="danger">{refusal}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" isLoading={busy}>
            Create
          </Button>
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
        </div>
      </form>
    </Dialog>
  );
}

function DuplicateBrand({ brand, onClose, run }: { brand: BrandSummary; onClose: () => void; run: (name: string) => Promise<void> }) {
  const [name, setName] = useState(`${brand.name} copy`);
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    await run(name);
    setBusy(false);
    onClose();
  }
  return (
    <Dialog title={`Duplicate ${brand.name}`} isOpen onOpenChange={(open) => !open && onClose()}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p>The copy starts as a draft holding this brand's current draft. It has no versions.</p>
        <TextField label="Name of the copy" value={name} onChange={setName} isRequired maxLength={100} />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" isLoading={busy}>
            Duplicate
          </Button>
          <Button onPress={onClose}>Cancel</Button>
        </div>
      </form>
    </Dialog>
  );
}

/** A decision that needs an answer (archive, delete): what will happen, and the button that does it. With
 * `typeToConfirm` the button stays off until the person has typed that name. */
function ConfirmAction({
  title,
  confirmLabel,
  typeToConfirm,
  onClose,
  run,
  children,
}: {
  title: string;
  confirmLabel: string;
  typeToConfirm?: string;
  onClose: () => void;
  run: () => Promise<void>;
  children: ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog role="alertdialog" title={title} isOpen onOpenChange={(open) => !open && onClose()}>
      <div className="flex flex-col gap-4">
        <p>{children}</p>
        {typeToConfirm !== undefined && <TextField label={`Type ${typeToConfirm} to confirm`} value={typed} onChange={setTyped} autoComplete="off" />}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="danger"
            isDisabled={typeToConfirm !== undefined && typed !== typeToConfirm}
            isLoading={busy}
            onPress={async () => {
              setBusy(true);
              await run();
              setBusy(false);
              onClose();
            }}
          >
            {confirmLabel}
          </Button>
          <Button onPress={onClose}>Cancel</Button>
        </div>
      </div>
    </Dialog>
  );
}
